import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Sprout, Star, Plus, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/api";
import { useCultures, regionOfParcel } from "@/lib/cultures";
import { toast } from "sonner";
import { SearchableSelect } from "./SearchableSelect";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Cultures déclarées de la parcelle (principale + secondaires) et production
 * estimée par culture. La culture principale pilote carte/filtres/doses via
 * culture_type (synchronisé côté backend).
 */
export default function CulturesSection({ parcel, onMainCultureChange }) {
  const initial = useMemo(() => {
    if (Array.isArray(parcel?.cultures) && parcel.cultures.length > 0) return parcel.cultures;
    return parcel?.culture_type
      ? [{ name: parcel.culture_type, main: true, estimated_production: null }]
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcel?.parcel_id]);

  const [cultures, setCultures] = useState(initial);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Cultures du référentiel de la région (en plus de celles de la parcelle)
  const known = useCultures(regionOfParcel(parcel?.parcel_id), { saisie: true });
  const [addFree, setAddFree] = useState("");
  const [medium, setMedium] = useState(parcel?.growing_medium || null);
  const [greenhouse, setGreenhouse] = useState(parcel?.greenhouse ?? null);
  const saveTimer = useRef(null);

  useEffect(() => {
    setMedium(parcel?.growing_medium || null);
    setGreenhouse(parcel?.greenhouse ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcel?.parcel_id]);

  const saveType = async (patch) => {
    const prev = { medium, greenhouse };
    if ("growing_medium" in patch) setMedium(patch.growing_medium);
    if ("greenhouse" in patch) setGreenhouse(patch.greenhouse);
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcel.parcel_id}/type`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        toast.error(typeof d.detail === "string" ? d.detail : "Enregistrement impossible");
        setMedium(prev.medium); setGreenhouse(prev.greenhouse);
      } else {
        if (parcel) Object.assign(parcel, patch);
        window.dispatchEvent(new CustomEvent("veyer:parcel-type-changed", { detail: { parcelId: parcel.parcel_id, ...patch } }));
      }
    } catch { toast.error("Erreur de connexion"); setMedium(prev.medium); setGreenhouse(prev.greenhouse); }
    setSaving(false);
  };

  useEffect(() => { setCultures(initial); }, [initial]);


  const persist = useCallback((next) => {
    setCultures(next);
    const main = next.find((c) => c.main);
    if (main) onMainCultureChange?.(main.name);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        const res = await authFetch(`${API_BASE}/parcels/${parcel.parcel_id}/cultures`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cultures: next }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          toast.error(typeof d.detail === "string" ? d.detail : "Enregistrement impossible");
        }
      } catch { toast.error("Erreur de connexion"); }
      setSaving(false);
    }, 600);
  }, [parcel?.parcel_id, onMainCultureChange]);

  const addCulture = (name) => {
    const n = (name || "").trim();
    if (!n) return;
    if (cultures.some((c) => c.name.toLowerCase() === n.toLowerCase())) {
      toast.info("Culture déjà déclarée");
      return;
    }
    persist([...cultures, { name: n, main: cultures.length === 0, estimated_production: null }]);
    setAddFree("");
  };

  const removeCulture = (name) => {
    let next = cultures.filter((c) => c.name !== name);
    if (!next.length) { toast.error("Au moins une culture est requise"); return; }
    if (!next.some((c) => c.main)) next = next.map((c, i) => ({ ...c, main: i === 0 }));
    persist(next);
  };

  const setMain = (name) => persist(cultures.map((c) => ({ ...c, main: c.name === name })));

  const setProd = (name, value, unit) => {
    persist(cultures.map((c) => c.name === name
      ? {
          ...c,
          estimated_production: value === "" || value === null
            ? null
            : { value: parseFloat(String(value).replace(",", ".")) || 0, unit },
        }
      : c));
  };

  const items = known.map((c) => ({ value: c, label: c }));

  return (
    <div className="shrink-0 border-b border-black/5 px-4 py-2" data-testid="cultures-section">
      {/* Barre compacte */}
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 text-left" data-testid="cultures-toggle">
        <Sprout className="w-3.5 h-3.5 shrink-0" style={{ color: "#2D6A4F" }} />
        <span className="text-[11px] font-semibold shrink-0" style={{ color: "#1A1A1A" }}>Cultures</span>
        <span className="flex items-center gap-1.5 flex-wrap min-w-0">
          {cultures.map((c) => (
            <span key={c.name} className="inline-flex items-center gap-1 text-[10.5px] font-medium rounded-full px-2 py-0.5"
              style={c.main ? { background: "#2D6A4F18", color: "#1f5c39" } : { background: "#0000000A", color: "#4A4A4A" }}>
              {c.main && <Star className="w-2.5 h-2.5 fill-current" />}{c.name}
            </span>
          ))}
        </span>
        {(medium || greenhouse !== null) && (
          <span className="flex items-center gap-1 shrink-0" data-testid="parcel-type-badges">
            {medium && (
              <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#8B5E3C18", color: "#6B4423" }}>
                {medium === "hors_sol" ? "Hors sol" : "En sol"}
              </span>
            )}
            {greenhouse !== null && (
              <span className="text-[10px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#0EA5E918", color: "#0369A1" }}>
                {greenhouse ? "Sous serre" : "Plein champ"}
              </span>
            )}
          </span>
        )}
        {saving && <Loader2 className="w-3 h-3 animate-spin shrink-0" style={{ color: "#7A7A7A" }} />}
        {open
          ? <ChevronUp className="w-3.5 h-3.5 ml-auto shrink-0" style={{ color: "#7A7A7A" }} />
          : <ChevronDown className="w-3.5 h-3.5 ml-auto shrink-0" style={{ color: "#7A7A7A" }} />}
      </button>

      {/* Panneau d'édition */}
      {open && (
        <div className="mt-2 space-y-2 pb-1">
          <div className="rounded-xl border border-black/10 px-3 py-2" data-testid="parcel-type-section">
            <div className="text-[11px] font-semibold mb-1.5" style={{ color: "#1A1A1A" }}>Type de parcelle</div>
            <div className="flex items-center gap-3 flex-wrap text-[11px]">
              <div className="inline-flex rounded-lg border border-black/15 overflow-hidden">
                {[["sol", "En sol"], ["hors_sol", "Hors sol"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => saveType({ growing_medium: v })}
                    className="px-2.5 h-7 font-semibold"
                    style={medium === v ? { background: "#6B4423", color: "#fff" } : { background: "#fff", color: "#4A4A4A" }}
                    data-testid={`parcel-medium-${v}`}>{l}</button>
                ))}
              </div>
              <div className="inline-flex rounded-lg border border-black/15 overflow-hidden">
                {[[false, "Plein champ"], [true, "Sous serre"]].map(([v, l]) => (
                  <button key={String(v)} type="button" onClick={() => saveType({ greenhouse: v })}
                    className="px-2.5 h-7 font-semibold"
                    style={greenhouse === v ? { background: "#0369A1", color: "#fff" } : { background: "#fff", color: "#4A4A4A" }}
                    data-testid={`parcel-greenhouse-${v}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          {cultures.map((c) => (
            <div key={c.name} className={`rounded-xl border px-3 py-2 ${c.main ? "border-emerald-600" : "border-black/10"}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12.5px] font-semibold" style={{ color: "#1A1A1A" }}>{c.name}</span>
                {c.main ? (
                  <span className="text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: "#2D6A4F18", color: "#1f5c39" }}>★ principale</span>
                ) : (
                  <>
                    <button onClick={() => setMain(c.name)} className="text-[10.5px] font-semibold border border-black/15 rounded-md px-2 py-0.5 hover:bg-black/5" style={{ color: "#4A4A4A" }} data-testid={`set-main-${c.name}`}>
                      Définir principale
                    </button>
                    <button onClick={() => removeCulture(c.name)} className="text-[10.5px] font-semibold border border-red-200 text-red-600 rounded-md px-2 py-0.5 hover:bg-red-50" data-testid={`remove-culture-${c.name}`}>
                      Retirer
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-dashed border-black/10 text-[11px]" style={{ color: "#7A7A7A" }}>
                Production estimée
                <input
                  defaultValue={c.estimated_production?.value ?? ""}
                  onBlur={(e) => setProd(c.name, e.target.value, c.estimated_production?.unit || "t/an")}
                  placeholder="—"
                  inputMode="decimal"
                  className="w-16 h-6 text-[11.5px] text-center border border-black/15 rounded-md bg-white"
                  data-testid={`prod-value-${c.name}`}
                />
                <select
                  value={c.estimated_production?.unit || "t/an"}
                  onChange={(e) => setProd(c.name, c.estimated_production?.value ?? "", e.target.value)}
                  className="h-6 text-[11px] border border-black/15 rounded-md bg-white px-1"
                  data-testid={`prod-unit-${c.name}`}
                >
                  <option value="t/an">t / an</option>
                  <option value="kg/an">kg / an</option>
                </select>
              </div>
            </div>
          ))}

          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-52">
              <SearchableSelect items={items} value="" onSelect={(v) => addCulture(v)} placeholder="+ Ajouter une culture…" testId="add-culture-select" />
            </div>
            <input
              value={addFree}
              onChange={(e) => setAddFree(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addCulture(addFree); }}
              placeholder="ou saisir librement"
              className="h-9 text-xs border border-black/15 rounded-md px-2 flex-1 min-w-[120px]"
              data-testid="add-culture-free"
            />
            <button onClick={() => addCulture(addFree)} className="h-9 px-3 rounded-md text-white" style={{ background: "#2D6A4F" }} data-testid="add-culture-btn" aria-label="Ajouter la culture saisie">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[10px] italic" style={{ color: "#9A948A" }}>
            La culture principale pilote la carte, les filtres et les recommandations. La production estimée servira aux estimations de valeur (app Mercuriales).
          </p>
        </div>
      )}
    </div>
  );
}
